import { Check, ChevronDown } from 'lucide-react';
import { CurrencyIcon } from 'src/modules/currencies/components/CurrencyIcon';
import { useCurrencyBySymbol } from 'src/modules/currencies/hooks/useCurrencyBySymbol';
import { getKnownCurrencyLogo } from 'src/modules/currencies/utils/knownCurrencyLogos';

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/design-system/components/ui';

export interface EarningsCurrencyOption {
  /** `currencies.id` — the value the BE's `earnings_currency_id` expects. */
  id: string;
  /** Display symbol, e.g. `USDC` / `POINT`. */
  name: string;
}

export interface EarningsSortHeaderProps {
  label: string;
  currencies: EarningsCurrencyOption[];
  /** Currency the table is currently sorted by, or null when sorted by another column. */
  activeCurrencyId: string | null;
  sortOrder: 'asc' | 'desc';
  /** Fires with the picked currency. Picking the active one should toggle the order. */
  onSelectCurrency: (currencyId: string) => void;
  /**
   * Chain the project settles on (`project.contractChainId`, as
   * PaymentCurrencySelector.tsx:34 passes it). Without it a symbol lookup can
   * resolve the same-named currency on another chain.
   */
  chainIdentifier?: string | null;
}

/**
 * Token icon for a currency known only by symbol. Same resolution the earnings
 * cell uses (MultiCurrencyEarnings.tsx:88-99), so header and cells never
 * disagree: symbols `getKnownCurrencyLogo` already covers (POINT, USDC, …) skip
 * the request entirely, the rest look the currency up on this project's chain.
 */
function CurrencyOptionIcon({
  name,
  size,
  chainIdentifier,
}: {
  name: string;
  size: number;
  chainIdentifier?: string | null;
}) {
  const knownLogoUri = getKnownCurrencyLogo(name);
  const { data: currency } = useCurrencyBySymbol(name, chainIdentifier, !knownLogoUri);
  return <CurrencyIcon logoUri={knownLogoUri ?? currency?.logoUri} name={name} size={size} />;
}

/**
 * Header for the sortable "Total earnings" column (FUU-1999).
 *
 * The BE sorts earnings within ONE currency (`earnings_currency_id`), so with
 * more than one payout currency the header has to ask which. There is no
 * existing sortable-header-with-picker in the repo — the closest analogs are the
 * interactive-child header at MasterAffiliatesTable.tsx:111-124 and the row
 * `DropdownMenu` at AffiliateTable.tsx:258-265, both of which this mirrors.
 *
 * Only used in the multi-currency case. With a single payout currency the column
 * is a plain sortable column and `DataTable` owns the click and the arrow
 * (data-table.tsx:697-725) — no picker, no second click to reach the sort.
 *
 * The ↑/↓ arrow is NOT drawn here in either case: `DataTable` renders it for
 * every column from the controlled `sortingState` it was given, regardless of
 * `canSort` (data-table.tsx:684-685, :721-722, and the prop contract at
 * :130-140). Drawing one here too is what produced `Total earnings·USDC↓↓`.
 */
export function EarningsSortHeader({
  label,
  currencies,
  activeCurrencyId,
  sortOrder,
  onSelectCurrency,
  chainIdentifier,
}: EarningsSortHeaderProps) {
  const active = currencies.find((currency) => currency.id === activeCurrencyId) ?? null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          // The `<th>` carries DataTable's sort handler whenever the column is
          // sortable; this column is not, but keep the guard so enabling sorting
          // later can't double-fire.
          onClick={(e) => e.stopPropagation()}
          className="tw:h-auto tw:px-0 tw:py-0 tw:font-normal tw:normal-case tw:tracking-normal tw:text-sm"
          // The direction lives in the aria-label because the only visual cue is
          // DataTable's arrow, which is `aria-hidden` (data-table.tsx:721-722).
          aria-label={
            active
              ? `${label}, sorted by ${active.name} ${sortOrder === 'desc' ? 'descending' : 'ascending'}. Change sort currency`
              : `${label}. Sort by currency`
          }
        >
          <span className="tw:inline-flex tw:items-center tw:gap-1">
            {label}
            {active && (
              <>
                <span className="tw:text-muted-foreground">·</span>
                <CurrencyOptionIcon name={active.name} size={14} chainIdentifier={chainIdentifier} />
                <span>{active.name}</span>
              </>
            )}
            <ChevronDown size={14} aria-hidden="true" />
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>Sort by earnings in</DropdownMenuLabel>
        {currencies.map((currency) => (
          <DropdownMenuItem
            key={currency.id}
            onClick={(e) => {
              e.stopPropagation();
              onSelectCurrency(currency.id);
            }}
          >
            <CurrencyOptionIcon name={currency.name} size={16} chainIdentifier={chainIdentifier} />
            {currency.name}
            {currency.id === activeCurrencyId && <Check size={14} className="tw:ml-auto" aria-hidden="true" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
